import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

function ProductSearchDropdown({ onSelect, excludeIds = [], onClose, hideOutOfStock = false, maxHeight = 450, maxQtyMap = {}, selectedDate = null, availabilityLabel = 'Доступно', includeArchived = false, limit = null, onProductsLoaded = null, products: productsProp = null }) {
  // includeArchived: грузить и активные, и архивные товары (для фильтров-поиска).
  // limit: показывать не более N отфильтрованных строк (короткий предпросмотр, поиск по всем).
  // maxQtyMap: { "productId": maxQty } — если задан, используется вместо product.quantity
  // onProductsLoaded: колбэк со списком загруженных товаров — чтобы вызывающий не грузил
  //   тот же список второй раз ради id (напр. для батча доступности).
  // products: уже загруженный список от вызывающего — дропдаун не делает собственный
  //   запрос (панель заказа: каталог + date-aware maxQtyMap уже есть, качать 500 товаров
  //   на каждое открытие незачем). null = грузим сами, как раньше.
  const [ownProducts, setOwnProducts] = useState([]);
  const products = productsProp !== null ? productsProp : ownProducts;
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(productsProp === null);
  const wrapperRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (productsProp !== null) return; // список дал вызывающий — свой запрос не нужен
    // Первую загрузку не задерживаем — debounce нужен только при смене даты.
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      loadProducts();
      return;
    }
    const timer = setTimeout(() => loadProducts(), 300);
    return () => clearTimeout(timer);
  }, [selectedDate, productsProp]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Escape закрывает только список — окно под ним остаётся (как у календаря)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const loadProducts = async () => {
    try {
      const data = includeArchived
        ? await api.getProductsAll(true)
        : await api.getProducts(1, 500, false, selectedDate);
      const list = data.items || data;
      setOwnProducts(list);
      if (onProductsLoaded) onProductsLoaded(list);
    } catch { setOwnProducts([]); }
    finally { setIsLoading(false); }
  };

  const getAvailableQty = (product) => {
    const pidStr = String(product.id);
    // Ключа в maxQtyMap нет — значит наличие неизвестно, а не ноль: карту мог собрать
    // вызывающий по более короткому списку товаров. Иначе при hideOutOfStock такой
    // товар молча пропадёт из перечня.
    if (maxQtyMap[pidStr] != null) {
      return maxQtyMap[pidStr];
    }
    // Если бэкенд вернул date_available (запрос с ?date=), используем его
    if (product.date_available != null) {
      return product.date_available;
    }
    const futureAvail = (product.future_stock || []).reduce((s, fs) => s + (fs.available || 0), 0);
    return (product.quantity || 0) + futureAvail;
  };

  const filtered = products
    .filter(p => !excludeIds.includes(p.id) && p.name.toLowerCase().includes(search.toLowerCase()))
    .filter(p => {
      if (!hideOutOfStock) return true;
      return getAvailableQty(p) > 0;
    })
    .sort((a, b) => {
      const aStock = getAvailableQty(a) > 0 ? 0 : 1;
      const bStock = getAvailableQty(b) > 0 ? 0 : 1;
      if (aStock !== bStock) return aStock - bStock;
      return a.name.localeCompare(b.name, 'ru');
    });

  // Короткий предпросмотр: поиск идёт по всем товарам, но рендерим максимум `limit`.
  const displayed = limit ? filtered.slice(0, limit) : filtered;

  return (
    <div ref={wrapperRef} style={{
      border: '1px solid #ddd', '--ch': '6px', '--stroke': '#ddd', background: '#fff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden'
    }}>
      <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск товара..."
          autoFocus
          style={{
            width: '100%', padding: '6px 8px', fontSize: 13,
            border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {isLoading && productsProp === null ? (
          <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>Загрузка...</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>Ничего не найдено</div>
        ) : displayed.map(product => {
          const alreadyInOrder = excludeIds.includes(product.id);
          const availQty = getAvailableQty(product);
          const noStock = availQty <= 0;
          const disabled = alreadyInOrder;

          return (
            <div key={product.id}
              onClick={() => !disabled && onSelect(product, availQty)}
              style={{
                padding: '8px 10px', cursor: disabled ? 'default' : 'pointer',
                borderBottom: '1px solid #F4F4EF',
                opacity: disabled ? 0.4 : 1,
                background: noStock && !disabled ? '#FEF6E6' : '#fff',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 13
              }}
              onMouseEnter={e => !disabled && (e.currentTarget.style.background = noStock ? '#F9EBCF' : '#F4F4EF')}
              onMouseLeave={e => e.currentTarget.style.background = noStock && !disabled ? '#FEF6E6' : '#fff'}
            >
              <div>
                <div style={{ fontWeight: 500, color: noStock ? '#9A4820' : undefined }}>{product.name}</div>
                <div style={{ fontSize: 11, color: noStock ? '#9A4820' : '#999' }}>
                  {product.price} ₽ {noStock ? '· Нет в наличии' : `· ${availabilityLabel}: ${availQty}`}
                  {alreadyInOrder && ' · Уже в заказе'}
                </div>
              </div>
            </div>
          );
        })}
        {!isLoading && limit && filtered.length > displayed.length && (
          <div style={{ padding: '8px 10px', textAlign: 'center', color: '#999', fontSize: 12 }}>
            Показаны первые {displayed.length}. Уточните запрос, чтобы найти нужный товар.
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductSearchDropdown;
